/**
 * LexPortal typed client for the per-user lexruntime OpenClaw settings
 * surface at `/api/lx/openclaw/*`. Every call routes through lexserver's
 * existing per-user-runtime path-based proxy (`/api/lx/*` → user's
 * lexruntime FQDN — see `ai-docs/per-user-runtime-proxy-plan.md`).
 *
 * Cross-user: this client is intended for PLATFORM_ADMIN flows
 * (Phase 21 of lexportal-implementation-plan.md). Every function takes
 * a `targetUserId` and sets the `X-Target-User-Id` request header. The
 * lexserver-side proxy resolves THAT user's runtime instead of the
 * JWT-holder's own (see lexserver/src/routes/runtime-proxy.ts §B). The
 * caller's JWT must carry the `platform.admin` scope or the proxy
 * rejects with `403 CROSS_USER_FORBIDDEN`.
 *
 * Canonical contract: `ai-docs/lexruntime-openclaw-settings-guide.md`
 * (especially §3 endpoint inventory + §7 error matrix).
 *
 * Handoff §10 hard rule: **never cache org/user settings in
 * localStorage**. Operator views may be cross-user; caching produces
 * stale-data incidents. Every read fetches fresh.
 *
 * Type sourcing: shapes mirror `lexide/src/api/lx-openclaw-config.ts`
 * (the LexIDE sibling owns the same surface for MEMBER flows). The
 * org-admin shared package may consolidate both clients in a future
 * pass — for now the types live in both places to keep the handoff
 * compile-clean independently.
 */

import { newTraceparent } from "@/lib/traceparent";

/** LexPortal proxies every backend call through `/lexserver/*` (see
 *  `lexportal/vite.config.ts` + the App Service serve.js rewrite). The
 *  ROOT here is therefore the full prefixed path — identical to the
 *  pattern PlatformApi uses with its LEXSERVER_BASE constant. The
 *  upstream lexserver mount strips `/lexserver` and the `/api/lx/*`
 *  catch-all in runtime-proxy.ts proxies to the target user's runtime. */
const ROOT = "/lexserver/api/lx/openclaw";
const TARGET_USER_HEADER = "X-Target-User-Id";

// ─── Tier-B-org (read-only from this surface) ─────────────────────────

export interface UserSettingsPolicy {
  personaSuffix?: boolean;
  locale?: boolean;
  channels?: {
    whatsapp?: boolean;
    telegram?: boolean;
    email?: boolean;
  };
  push?: boolean;
  browser?: boolean;
  voice?: boolean;
  agentsMain?: boolean;
}

export interface OrgVoiceConfig {
  provider?: string;
  allowedVoices?: string[];
  defaultVoice?: string;
  rate?: number;
}

export interface OrgAgentsConfig {
  defaultIdentity?: {
    name?: string;
    avatarUrl?: string;
  };
}

export interface OrgOpenclawConfig {
  userSettingsPolicy?: UserSettingsPolicy;
  agents?: OrgAgentsConfig;
  voice?: OrgVoiceConfig;
  browser?: Record<string, unknown>;
  memory?: Record<string, unknown>;
  tools?: Record<string, unknown>;
}

// ─── Tier-B-user ──────────────────────────────────────────────────────

export interface ChannelState {
  enabled?: boolean;
  status?: "disconnected" | "pending" | "connected" | "failed";
  connectedAt?: string;
  lastSync?: string;
  lastError?: string;
  deviceLabel?: string;
}

export interface PushSubscription {
  id: string;
  endpoint?: string;
  deviceLabel?: string;
  browser?: string;
  createdAt?: string;
}

export interface BrowserProfile {
  id: string;
  label: string;
  userDataDir: string;
  createdAt?: string;
  lastUsedAt?: string;
  notes?: string;
}

export interface UserState {
  schemaVersion?: number;
  personaSuffix?: string;
  locale?: string;
  channels?: {
    whatsapp?: ChannelState;
    telegram?: ChannelState;
    email?: ChannelState;
  };
  push?: {
    enabled?: boolean;
    subscriptions?: PushSubscription[];
  };
  browser?: {
    enabled?: boolean;
    activeProfileId?: string | null;
    profiles?: BrowserProfile[];
  };
  voice?: {
    tts?: {
      enabled?: boolean;
      selectedVoice?: string;
    };
  };
  agents?: {
    main?: {
      name?: string;
      avatarUrl?: string;
    };
  };
}

export type UserStatePatch = Partial<UserState>;

export interface OpenclawConfig {
  org: OrgOpenclawConfig;
  user: UserState | null;
}

// ─── Channels / Push / Cron / Browser response shapes ─────────────────

export interface ChannelConnectResult {
  qrPng?: string;
  sessionToken?: string;
  note?: string;
}

export interface ChannelStatusResult {
  enabled?: boolean;
  status?: ChannelState["status"];
  lastSync?: string;
  lastError?: string;
}

export interface VapidPublicKeyResult {
  publicKey: string;
}

export interface PushEvent {
  id: string;
  type: "subscribed" | "unsubscribed" | "test_sent" | "delivered" | "clicked" | "dismissed" | "failed";
  subscriptionId?: string;
  deviceLabel?: string;
  timestamp: string;
  payload?: Record<string, unknown>;
}

export interface CronJob {
  jobId: string;
  label: string;
  schedule: string;
  timezone?: string;
  promptOrTask: string;
  delivery?: {
    channelId?: string;
    targetWhatsapp?: string;
    targetEmail?: string;
  };
  enabled?: boolean;
  lastRunStatus?: "ok" | "failed" | "running" | "cancelled";
  nextRunAt?: string;
  lastRunAt?: string;
  createdBy?: string;
}

export interface CronRun {
  runId: string;
  jobId: string;
  startedAt: string;
  finishedAt?: string;
  status: "ok" | "failed" | "running" | "cancelled";
  output?: string;
  error?: string;
}

export interface BrowserStatus {
  enabled: boolean;
  activeProfileId: string | null;
  vncEndpoint?: string;
  vncTokenTtlMs?: number;
  vnc?: { endpoint?: string; note?: string };
}

/** GET /browser/vnc-ticket — the runtime mints a short-TTL token the pane
 *  appends to its WSS URL as `?token=<value>` (websockify validates it). The
 *  pane re-mints every `ttlMs - 10s`. Shape MUST match the runtime + lexide
 *  (`@/api/lx-openclaw-config` in lexide) — the pre-P4.D.W `{ jwt }` shape was
 *  never wired to a live pane and was wrong. */
export interface VncTicket {
  token: string;
  expiresAt: string;
  ttlMs: number;
}

// ─── Errors ───────────────────────────────────────────────────────────

export class LxOpenclawConfigError extends Error {
  status: number;
  code?: string;
  /** Populated on 409 USER_SETTING_DISABLED_BY_ORG. */
  disabledKeys?: string[];
  /** The target user this call was scoped to — surfaces in toasts so the
   *  operator sees WHICH user's runtime threw. */
  targetUserId: string;
  constructor(
    message: string,
    status: number,
    targetUserId: string,
    code?: string,
    disabledKeys?: string[],
  ) {
    super(message);
    this.name = "LxOpenclawConfigError";
    this.status = status;
    this.code = code;
    this.disabledKeys = disabledKeys;
    this.targetUserId = targetUserId;
  }
}

// ─── Plumbing ─────────────────────────────────────────────────────────

export type TokenGetter = () => Promise<string | null>;

interface ErrorEnvelope {
  ok?: false;
  error?: string | { code?: string; message?: string };
  message?: string;
  disabledKeys?: string[];
}

async function authedHeaders(
  getToken: TokenGetter,
  targetUserId: string,
): Promise<Record<string, string>> {
  const token = await getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    [TARGET_USER_HEADER]: targetUserId,
    // Fresh W3C trace per request so lexserver's sentry-scope middleware
    // and the lexruntime-side audit row share one trace_id with the
    // call's downstream hops. Mirrors lib/platform-api.ts:218.
    traceparent: newTraceparent(),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function throwFromResponse(
  res: Response,
  route: string,
  targetUserId: string,
): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as ErrorEnvelope;
  let code: string | undefined;
  let message: string | undefined;
  if (typeof body.error === "string") {
    code = body.error;
    message = body.message;
  } else if (body.error && typeof body.error === "object") {
    code = body.error.code;
    message = body.error.message ?? body.message;
  } else {
    message = body.message;
  }
  throw new LxOpenclawConfigError(
    message ?? `${route} failed: ${res.status}`,
    res.status,
    targetUserId,
    code,
    body.disabledKeys,
  );
}

async function unwrapJson<T>(res: Response, route: string, targetUserId: string): Promise<T> {
  if (!res.ok) await throwFromResponse(res, route, targetUserId);
  const body = (await res.json()) as { data?: T };
  if (body.data === undefined) {
    throw new LxOpenclawConfigError(
      `${route} returned no data field`,
      res.status,
      targetUserId,
      "MALFORMED_RESPONSE",
    );
  }
  return body.data;
}

// ─── Config: read + write ─────────────────────────────────────────────

export async function getOpenclawConfig(
  getToken: TokenGetter,
  targetUserId: string,
): Promise<OpenclawConfig> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/config/get`, { method: "GET", headers });
  return unwrapJson<OpenclawConfig>(res, "GET /config/get", targetUserId);
}

export async function patchOpenclawUserConfig(
  getToken: TokenGetter,
  targetUserId: string,
  patch: UserStatePatch,
): Promise<UserState> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/config/user`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(patch),
  });
  // The patch endpoint envelope wraps the user state; handle the shape
  // explicitly so callers don't see `{ user: ... }` indirection.
  if (!res.ok) await throwFromResponse(res, "PATCH /config/user", targetUserId);
  const body = (await res.json()) as { data?: { user?: UserState } };
  return body.data?.user ?? {};
}

// ─── Channels ─────────────────────────────────────────────────────────

export type ChannelId = "whatsapp" | "telegram" | "email";

export async function connectChannel(
  getToken: TokenGetter,
  targetUserId: string,
  channelId: ChannelId,
): Promise<ChannelConnectResult> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/config/channels/${channelId}/connect`, {
    method: "POST",
    headers,
  });
  return unwrapJson<ChannelConnectResult>(
    res,
    `POST /config/channels/${channelId}/connect`,
    targetUserId,
  );
}

export async function getChannelConnectStatus(
  getToken: TokenGetter,
  targetUserId: string,
  channelId: ChannelId,
): Promise<ChannelStatusResult> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/config/channels/${channelId}/connect/status`, {
    method: "GET",
    headers,
  });
  return unwrapJson<ChannelStatusResult>(
    res,
    `GET /config/channels/${channelId}/connect/status`,
    targetUserId,
  );
}

export async function getChannelStatus(
  getToken: TokenGetter,
  targetUserId: string,
  channelId: ChannelId,
): Promise<ChannelStatusResult> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/config/channels/${channelId}/status`, {
    method: "GET",
    headers,
  });
  return unwrapJson<ChannelStatusResult>(
    res,
    `GET /config/channels/${channelId}/status`,
    targetUserId,
  );
}

/** Disconnect a channel — also the operator-grade "force-clear stored
 *  session" primitive (§4.2 of the handoff). The endpoint is idempotent
 *  and identical for user-self vs operator flows; the UX difference
 *  lives in the dialog. */
export async function disconnectChannel(
  getToken: TokenGetter,
  targetUserId: string,
  channelId: ChannelId,
): Promise<{ ok: boolean }> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/config/channels/${channelId}/disconnect`, {
    method: "POST",
    headers,
  });
  if (!res.ok)
    await throwFromResponse(
      res,
      `POST /config/channels/${channelId}/disconnect`,
      targetUserId,
    );
  return { ok: true };
}

// ─── Push ─────────────────────────────────────────────────────────────

export async function getVapidPublicKey(
  getToken: TokenGetter,
  targetUserId: string,
): Promise<VapidPublicKeyResult> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/config/push/vapid-public-key`, {
    method: "GET",
    headers,
  });
  return unwrapJson<VapidPublicKeyResult>(
    res,
    "GET /config/push/vapid-public-key",
    targetUserId,
  );
}

export async function testPushSubscription(
  getToken: TokenGetter,
  targetUserId: string,
  subscriptionId: string,
): Promise<{ ok: boolean }> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/config/push/test`, {
    method: "POST",
    headers,
    body: JSON.stringify({ id: subscriptionId }),
  });
  if (!res.ok) await throwFromResponse(res, "POST /config/push/test", targetUserId);
  return { ok: true };
}

export async function unsubscribePush(
  getToken: TokenGetter,
  targetUserId: string,
  subscriptionId: string,
): Promise<{ ok: boolean }> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/config/push/unsubscribe`, {
    method: "POST",
    headers,
    body: JSON.stringify({ id: subscriptionId }),
  });
  if (!res.ok)
    await throwFromResponse(res, "POST /config/push/unsubscribe", targetUserId);
  return { ok: true };
}

export async function getRecentPushEvents(
  getToken: TokenGetter,
  targetUserId: string,
): Promise<{ events: PushEvent[] }> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/push-events/recent`, { method: "GET", headers });
  return unwrapJson<{ events: PushEvent[] }>(
    res,
    "GET /push-events/recent",
    targetUserId,
  );
}

// ─── Cron ─────────────────────────────────────────────────────────────

export async function listCronJobs(
  getToken: TokenGetter,
  targetUserId: string,
): Promise<{ jobs: CronJob[] }> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/config/cron/jobs`, { method: "GET", headers });
  return unwrapJson<{ jobs: CronJob[] }>(res, "GET /config/cron/jobs", targetUserId);
}

export async function getCronJobRuns(
  getToken: TokenGetter,
  targetUserId: string,
  jobId: string,
): Promise<{ runs: CronRun[] }> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/config/cron/jobs/${encodeURIComponent(jobId)}/runs`, {
    method: "GET",
    headers,
  });
  return unwrapJson<{ runs: CronRun[] }>(
    res,
    `GET /config/cron/jobs/${jobId}/runs`,
    targetUserId,
  );
}

export async function runCronJob(
  getToken: TokenGetter,
  targetUserId: string,
  jobId: string,
): Promise<{ runId: string }> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/config/cron/jobs/${encodeURIComponent(jobId)}/run`, {
    method: "POST",
    headers,
  });
  return unwrapJson<{ runId: string }>(
    res,
    `POST /config/cron/jobs/${jobId}/run`,
    targetUserId,
  );
}

/** Operator-grade force-cancel (handoff §4.4). The endpoint is added
 *  in lexruntime P6 — best-effort: harness-relayed jobs may not
 *  interrupt mid-LLM-call. Until P6 ships the runtime returns 501
 *  NOT_IMPLEMENTED; surface that to the operator. */
export async function cancelCronJob(
  getToken: TokenGetter,
  targetUserId: string,
  jobId: string,
): Promise<{ ok: boolean }> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/config/cron/jobs/${encodeURIComponent(jobId)}/cancel`, {
    method: "POST",
    headers,
  });
  if (!res.ok)
    await throwFromResponse(
      res,
      `POST /config/cron/jobs/${jobId}/cancel`,
      targetUserId,
    );
  return { ok: true };
}

// ─── Browser ──────────────────────────────────────────────────────────

export async function listBrowserProfiles(
  getToken: TokenGetter,
  targetUserId: string,
): Promise<{ browser: { profiles: BrowserProfile[] } }> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/browser/profiles`, { method: "GET", headers });
  return unwrapJson<{ browser: { profiles: BrowserProfile[] } }>(
    res,
    "GET /browser/profiles",
    targetUserId,
  );
}

export async function getBrowserStatus(
  getToken: TokenGetter,
  targetUserId: string,
): Promise<BrowserStatus> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/browser/status`, { method: "GET", headers });
  return unwrapJson<BrowserStatus>(res, "GET /browser/status", targetUserId);
}

export async function launchBrowserProfile(
  getToken: TokenGetter,
  targetUserId: string,
  profileId: string,
): Promise<BrowserStatus> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(
    `${ROOT}/browser/profiles/${encodeURIComponent(profileId)}/launch`,
    { method: "POST", headers },
  );
  return unwrapJson<BrowserStatus>(
    res,
    `POST /browser/profiles/${profileId}/launch`,
    targetUserId,
  );
}

/** Stop a profile. Operator-grade "force-stop while user is active"
 *  (handoff §4.1) uses the SAME endpoint — it's already idempotent. The
 *  dialog wrapping the call carries the operator-warning copy. */
export async function stopBrowserProfile(
  getToken: TokenGetter,
  targetUserId: string,
  profileId: string,
): Promise<BrowserStatus> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(
    `${ROOT}/browser/profiles/${encodeURIComponent(profileId)}/stop`,
    { method: "POST", headers },
  );
  return unwrapJson<BrowserStatus>(
    res,
    `POST /browser/profiles/${profileId}/stop`,
    targetUserId,
  );
}

export async function getVncTicket(
  getToken: TokenGetter,
  targetUserId: string,
): Promise<VncTicket> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(`${ROOT}/browser/vnc-ticket`, { method: "GET", headers });
  return unwrapJson<VncTicket>(res, "GET /browser/vnc-ticket", targetUserId);
}

// ─── Browser P10 — operator-grade flow control ───────────────────────
//
// These three endpoints ship in the lexruntime-side P10 slice
// coordinated 2026-05-31 (see the handoff hand-back in this lane). They
// were pre-wired here so the LexPortal Browser tab UI is ready the
// moment the runtime image rolls; they will 404 against any runtime
// build that predates that slice — the tab surfaces the error and the
// operator retries after the upgrade.
//
//   pause / resume — flip an agentPaused flag the conditional GATE
//   consults. The runtime stamps `reason = "agent-paused-by-operator"`
//   when the caller carries `platform.admin` scope (vs
//   `"agent-paused-by-user"` for the MEMBER-self LexIDE path).
//
//   wipeBrowserProfileCookies — `rm -rf <userDataDir>`. UI MUST wrap
//   this in a typed-confirm gate (handoff §8 threat-model note J: the
//   userDataDir is the only cookie-revocation primitive in v1).

export async function pauseBrowserProfile(
  getToken: TokenGetter,
  targetUserId: string,
  profileId: string,
): Promise<BrowserStatus> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(
    `${ROOT}/browser/profiles/${encodeURIComponent(profileId)}/pause`,
    { method: "POST", headers },
  );
  return unwrapJson<BrowserStatus>(
    res,
    `POST /browser/profiles/${profileId}/pause`,
    targetUserId,
  );
}

export async function resumeBrowserProfile(
  getToken: TokenGetter,
  targetUserId: string,
  profileId: string,
): Promise<BrowserStatus> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(
    `${ROOT}/browser/profiles/${encodeURIComponent(profileId)}/resume`,
    { method: "POST", headers },
  );
  return unwrapJson<BrowserStatus>(
    res,
    `POST /browser/profiles/${profileId}/resume`,
    targetUserId,
  );
}

export async function wipeBrowserProfileCookies(
  getToken: TokenGetter,
  targetUserId: string,
  profileId: string,
): Promise<{ ok: boolean }> {
  const headers = await authedHeaders(getToken, targetUserId);
  const res = await fetch(
    `${ROOT}/browser/profiles/${encodeURIComponent(profileId)}/wipe-cookies`,
    { method: "POST", headers },
  );
  if (!res.ok)
    await throwFromResponse(
      res,
      `POST /browser/profiles/${profileId}/wipe-cookies`,
      targetUserId,
    );
  return { ok: true };
}
